import React, { memo, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDrag } from 'react-dnd';
import { Document, Page, pdfjs } from 'react-pdf';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import compose from 'lodash/fp/compose';
import ListDocumentLottie from 'lottie-react';
import { documentSlice } from 'rootStateHandler/selectors';
import animatedLoading from 'assets/lottie/admit-cat-loader.json';
import { withTranslation } from '../../../hoc';
import { editIcon, deleteIcon, viewIcon } from '../../../../libs/icon';
import EmptyState from 'components/common/EmptyState';
import './listDocuments.css';

// Serve the pdf.js worker from our own bundle rather than a CDN.
// pdfjs-dist v4+ ships the worker as ESM only (pdf.worker.min.mjs); the old
// `pdf.worker.js` path no longer exists at any version, so the CDN URL this
// used to build returned 404 and every preview failed with "Setting up fake
// worker failed". Resolving it through the bundler also pins the worker to the
// exact pdfjs-dist version react-pdf was built against, so the two can never
// drift again, and avoids a protocol-relative `//` URL that would be blocked
// as mixed content once served over https.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// Utility functions
const getFileExtension = (filename) => filename.split('.').pop() || '';

// The API already returns short-lived, opaque CloudFront signed URLs on
// download.admincat.net. They must be used verbatim: rewriting the path or
// appending query parameters invalidates the signature and CloudFront answers
// 403.
const secureUrl = (url) => url || '';
const isImageFile = (filename) =>
  ['gif', 'png', 'jpg', 'jpeg'].includes(getFileExtension(filename).toLowerCase());

const isOfficeFile = (filename) =>
  ['docx', 'doc', 'xlsx', 'xls', 'csv'].includes(
    getFileExtension(filename).toLowerCase(),
  );

const isPdfFile = (filename) => getFileExtension(filename).toLowerCase() === 'pdf';

const getOfficeFileIcon = (filename) => {
  const ext = getFileExtension(filename).toLowerCase();
  switch (ext) {
    case 'docx':
    case 'doc':
      return '📄'; // Word document icon
    case 'xlsx':
    case 'xls':
      return '📊'; // Excel spreadsheet icon
    case 'csv':
      return '📋'; // CSV file icon
    default:
      return '📁'; // Generic file icon
  }
};
const truncateString = (str, len) =>
  !str
    ? ''
    : str.length >= len
    ? `${str.substring(0, len)}...${getFileExtension(str)}`
    : str;

const LoadingState = ({ tr }) => {
  const [showContent, setShowContent] = useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  return showContent ? (
    <div className="lottie_loader_document">
      <ListDocumentLottie loop autoplay animationData={animatedLoading} />
      <div className="loading_text">{tr('Loading...')}</div>
    </div>
  ) : null;
};

const EmptyStateWrapper = ({ text }) => (
  <div className="empty_state_container">
    <EmptyState text={text} hasButton={false} />
  </div>
);

const PlaceholderPreview = ({ width, height, children }) => (
  <div
    className="unknown-file-preview"
    style={{
      width,
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '28px',
    }}
  >
    {children}
  </div>
);

const LazyImage = memo(({ src, alt, className, width, height, fallback = '📄' }) => {
  // No extra query parameters: they are not part of the signed resource.
  const imageSrc = useMemo(() => secureUrl(src), [src]);

  // An empty src renders as a broken image; show the type icon instead.
  if (!imageSrc) {
    return (
      <PlaceholderPreview width={width} height={height}>
        {fallback}
      </PlaceholderPreview>
    );
  }

  return (
    <div style={{ width, height }}>
      <LazyLoadImage
        alt={alt}
        height={height}
        src={imageSrc}
        width={width}
        className={className}
        effect="blur"
      />
    </div>
  );
});

// Defined once at module scope: react-pdf compares `options` by identity and
// re-downloads the document whenever a new object is passed, which is what the
// "Options prop passed to <Document /> changed" warning is about.
//
// No cMapUrl: it pointed at a cdnjs path that returns 403, and cMaps only
// affect PDFs with non-embedded CJK encodings. These previews are 99px
// thumbnails of page 1 with the text layer disabled, so they gain nothing from
// them. If a full PDF viewer with text selection is added later, vendor the
// cmaps from pdfjs-dist into public/ and point cMapUrl at "/cmaps/" - do not
// reintroduce a CDN.
const PDF_OPTIONS = {
  disableAutoFetch: true,
  disableStream: true,
};

const PDFPreview = memo(({ file }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const cloudFrontFile = useMemo(() => secureUrl(file), [file]);
  // Same identity rule as options: a fresh object each render makes react-pdf
  // reload the PDF on every render.
  const fileProp = useMemo(() => ({ url: cloudFrontFile }), [cloudFrontFile]);

  return (
    <div className="pdf-wrapper">
      <Document
        file={fileProp}
        loading={null}
        onLoadSuccess={() => setIsLoaded(true)}
        onLoadError={(error) => {
          console.error('PDF loading error:', error);
          setError(error);
        }}
        options={PDF_OPTIONS}
      >
        {isLoaded && (
          <Page
            pageNumber={1}
            width={99}
            className="pdf-page"
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        )}
      </Document>
      {error && <div className="pdf-error">Failed to load</div>}
    </div>
  );
});

const OfficePreview = memo(({ filename, snippet }) => {
  const icon = getOfficeFileIcon(filename);

  const optimizedSnippetUrl = useMemo(
    () => (snippet ? secureUrl(snippet) : null),
    [snippet],
  );

  // If we have a thumbnail (snippet), show it; otherwise show icon
  if (snippet) {
    return (
      <LazyImage
        src={optimizedSnippetUrl}
        alt="Office document thumbnail"
        className="preview-content office-thumbnail"
        width={99}
        height={136}
        fallback={icon}
      />
    );
  }

  // Fallback to icon if no thumbnail available
  return (
    <div
      className="office-icon-preview"
      style={{
        width: 99,
        height: 136,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '32px',
      }}
    >
      {icon}
    </div>
  );
});

const DocumentPreviewItem = memo(
  ({ document, isSelected, onSelect, onEdit, onDelete }) => {
    const { showViewOptions } = useSelector(documentSlice); // Get current tab

    const optimizedFileUrl = useMemo(
      () => secureUrl(document.file),
      [document.file],
    );
    const optimizedSnippetUrl = useMemo(
      () => secureUrl(document.snippet),
      [document.snippet],
    );
    // Never rebuild an S3 URL from the bucket name and the filename: the
    // bucket is private and the object key is not derivable from the
    // filename. Use the signed URL the API issued for this document.
    const downloadUrl = useMemo(
      () => secureUrl(document.download_url || document.file),
      [document.download_url, document.file],
    );

    const isFolderTab = showViewOptions === 'folderOptions';

    const [{ isDragging }, drag] = useDrag(
      () => ({
        type: 'document',
        item: { id: document.id, currentFolderId: document.folder?.id || null },
        canDrag: isFolderTab, // THIS IS THE KEY: Only drag in Folder Tab
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
        }),
      }),
      [isFolderTab],
    ); // Re-run when tab changes

    return (
      <div
        ref={isFolderTab ? drag : null} // Only attach drag ref in Folder Tab
        className="document_preview_container"
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: isFolderTab ? 'move' : 'pointer',
        }}
      >
        <div
          className="document_preview"
          aria-selected={isSelected}
          onClick={() => onSelect(document.id)}
        >
          {isImageFile(document.filename) ? (
            // An image document is its own thumbnail: fall back to the file
            // when no generated snippet exists, rather than showing nothing.
            <LazyImage
              src={optimizedSnippetUrl || optimizedFileUrl}
              alt={document.filename || 'document'}
              className="preview-content"
              width={99}
              height={136}
              fallback="🖼️"
              key={`img-${document.id}`}
            />
          ) : isOfficeFile(document.filename) ? (
            <OfficePreview
              filename={document.filename}
              snippet={document.snippet}
              key={`office-${document.id}`}
            />
          ) : isPdfFile(document.filename) ? (
            <PDFPreview file={optimizedFileUrl} key={`pdf-${document.id}`} />
          ) : (
            // Fallback for unknown file types
            <div
              className="unknown-file-preview"
              style={{
                width: 99,
                height: 136,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '28px',
              }}
            >
              📄
            </div>
          )}

          {document.id && (
            <div className={isSelected ? 'popIcon' : 'hideIcon'}>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                {editIcon}
              </span>
              <span>
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                  {viewIcon}
                </a>
              </span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                {deleteIcon}
              </span>
            </div>
          )}
        </div>
        <p
          className="documentTitle"
          style={{ color: 'var(--admincat-color-grey-2)' }}
        >
          {truncateString(document.filename, 10)}
        </p>
      </div>
    );
  },
);

const DocumentPreviews = memo(
  ({ documents, selectedId, onSelect, onEdit, onDelete }) => (
    <div className="list_documents_container">
      {documents.map((document) => (
        <DocumentPreviewItem
          key={document.id}
          document={document}
          isSelected={selectedId === document.id}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  ),
);

const FilteredDocuments = memo(
  ({ documents, selectedId, onSelect, onEdit, onDelete }) => (
    <div className="list_documents_container">
      {documents.map((document) => (
        <DocumentPreviewItem
          key={document.id}
          document={document}
          isSelected={selectedId === document.id}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  ),
);

const FullTextSearchDocuments = memo(
  ({ documents, selectedId, onSelect, onEdit, onDelete, tr }) => (
    <>
      <p style={{ color: 'var(--admincat-color-grey-2)' }}>
        {tr('Results found')}: {documents.length} {tr('result(s)')}
      </p>

      <div className="list_documents_container">
        {documents.map((document) => (
          <DocumentPreviewItem
            key={document.id}
            document={document}
            isSelected={selectedId === document.id}
            onSelect={onSelect}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </>
  ),
);

const ListDocuments = (props) => {
  const {
    handleDisplayInformation,
    isSearching,
    document_id,
    handleDelete,
    handleEdit,
    handleViewDocument,
    tr,
  } = props;

  const {
    filtere_documents,
    loading,
    list_documents,
    search_result,
    isFiltering,
    minimumLoadTimeElapsed,
    showViewOptions,
    error,
  } = useSelector(documentSlice);

  // console.log('list_documents: ', JSON.stringify(list_documents, null, 2));

  const isFolderTab = showViewOptions === 'folderOptions';

  const filteredDocuments = useMemo(
    () => [...filtere_documents],
    [filtere_documents],
  );

  const DocumentSearchPreviews = useMemo(() => {
    if (loading || !minimumLoadTimeElapsed) return <LoadingState tr={tr} />;
    if (!search_result?.length)
      return <EmptyStateWrapper text={tr('No result was found...')} />;

    return (
      <FullTextSearchDocuments
        documents={search_result}
        selectedId={document_id}
        onSelect={handleDisplayInformation}
        onEdit={handleEdit}
        onDelete={handleDelete}
        tr={tr}
      />
    );
  }, [loading, minimumLoadTimeElapsed, search_result, tr, handleDisplayInformation]);

  const renderMainDocuments = useMemo(() => {
    if (loading || !minimumLoadTimeElapsed) return <LoadingState tr={tr} />;
    if (list_documents.length === 0)
      return (
        <EmptyStateWrapper
          text={tr(
            isFolderTab ? 'This folder is empty' : 'No document uploaded yet!',
          )}
        />
      );

    return (
      <DocumentPreviews
        documents={list_documents}
        selectedId={document_id}
        onSelect={handleDisplayInformation}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    );
  }, [
    loading,
    minimumLoadTimeElapsed,
    list_documents,
    document_id,
    tr,
    handleDisplayInformation,
    handleEdit,
    handleDelete,
  ]);

  const renderFilteredDocuments = useMemo(() => {
    if (loading || !minimumLoadTimeElapsed) return <LoadingState tr={tr} />;
    if (!filteredDocuments?.length)
      return <EmptyStateWrapper text={tr('No result')} />;

    return (
      <FilteredDocuments
        documents={filteredDocuments}
        selectedId={document_id}
        onSelect={handleDisplayInformation}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleViewDocument}
        tr={tr}
      />
    );
  }, [
    loading,
    minimumLoadTimeElapsed,
    filteredDocuments,
    document_id,
    tr,
    handleDisplayInformation,
    handleEdit,
    handleDelete,
    handleViewDocument,
  ]);

  return (
    <>
      {isFiltering && renderFilteredDocuments}
      {!isSearching && !isFiltering && renderMainDocuments}
      {isSearching && DocumentSearchPreviews}
    </>
  );
};

export default compose(withTranslation)(ListDocuments);
